import cn from 'classnames'
import {motion} from 'framer-motion'
import type {CarouselSliceEventHero} from 'prismicio-types'
import type {FC} from 'react'
import {useState} from 'react'
import {useProgressBarRef} from 'slices/Carousel/useProgressBarRef'
import {Icon} from 'src/common/Icon'
import 'swiper/css'
import {Autoplay} from 'swiper/modules'
import type {SwiperClass} from 'swiper/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {EventSlide} from './EventSlide'

type EventHeroSliderProps = {
  events: CarouselSliceEventHero['primary']['events']
  duration: CarouselSliceEventHero['primary']['duration']
}

export const EventHeroSlider: FC<EventHeroSliderProps> = ({events, duration}) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const {progressBarRef, updateProgress} = useProgressBarRef()

  const elementsArray = Array.from({length: events.length}, (_, i) => i)

  return (
    <div className="flex w-full flex-col items-start gap-0">
      <div className="relative w-full">
        <Swiper
          autoplay={{
            delay: (duration ?? 8) * 1000,
          }}
          onAutoplayTimeLeft={(_, __, progress) => {
            updateProgress(progress)
          }}
          onActiveIndexChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          slidesPerView={1}
          spaceBetween={24}
          modules={[Autoplay]}
          breakpoints={{
            1024: {
              slidesPerView: 'auto',
              centeredSlides: true,
              spaceBetween: 24,
            },
          }}
          className="!z-0 w-full lg:!overflow-visible"
          onSwiper={(swiper) => setSwiperInstance(swiper)}
        >
          {events.map((eventItem, index) => (
            <SwiperSlide key={index} className="!w-full lg:!w-full lg:max-w-[1200px]">
              <EventSlide event={eventItem} index={index} activeIndex={activeIndex} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className="box-border flex w-full items-center justify-center gap-12 text-clip rounded-4 bg-moss-grey-100 pb-8 pt-6 md:pt-8">
        <button
          onClick={() => {
            if (swiperInstance) {
              if (activeIndex === 0) {
                // Loop to last slide
                swiperInstance.slideTo(events.length - 1)
              } else {
                swiperInstance.slidePrev()
              }
            }
          }}
          className="flex items-center justify-center"
          aria-label="Previous slide"
        >
          <Icon name="arrow" className="h-3 w-[13.595px] rotate-180 text-moss-grey-500" />
        </button>

        <div className="flex items-center gap-2">
          {elementsArray.map((_, index) => (
            <div
              key={`nav-dot-${index}`}
              className={cn('relative flex h-2 items-center text-clip rounded-[1px] bg-moss-grey-150', {
                'w-2': index !== activeIndex,
                'w-8': index === activeIndex,
              })}
            >
              {index === activeIndex && (
                <motion.div
                  ref={progressBarRef}
                  className="absolute z-10 h-2 w-full origin-left rounded-[1px] bg-neon-brand-450"
                />
              )}
            </div>
          ))}
        </div>

        <button
          onClick={() => {
            if (swiperInstance) {
              if (activeIndex === events.length - 1) {
                // Loop to first slide
                swiperInstance.slideTo(0)
              } else {
                swiperInstance.slideNext()
              }
            }
          }}
          className="flex items-center justify-center"
          aria-label="Next slide"
        >
          <Icon name="arrow" className="h-3 w-[13.595px] text-moss-grey-500" />
        </button>
      </div>
    </div>
  )
}
